import React, { useRef } from 'react';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@rocco/components/accordion';

/**
 * Interface representing a single FAQ element with title, question and answer
 * @interface FAQElement
 * @property {string} content - The full content of the FAQ
 * @property {string} title - The title/heading of the FAQ
 * @property {string} question - The FAQ question text
 * @property {string} answer - The FAQ answer text
 */
interface FAQElement {
    content: string;
    title: string;
    question?: string;
    answer?: string;
}

/**
 * Props interface for the CategoryPageFAQ component
 * @interface CategoryPageFAQProps
 * @property {FAQElement[]} faqSchemas - Array of FAQ items to display
 * @property {string} footerContent - HTML content to display in the footer section
 */
interface CategoryPageFAQProps {
    faqSchemas: FAQElement[];
    footerContent: string;
}

/**
 * Component that renders an FAQ section with expandable accordion items
 * Displays FAQ questions/answers and optional footer content
 * @param {CategoryPageFAQProps} props - Component props
 * @returns {JSX.Element | null} The rendered FAQ section or null if no content
 */
export const CategoryPageFAQ = (props: CategoryPageFAQProps) => {
    const { faqSchemas, footerContent } = props;
    const openedItemRef = useRef<HTMLDivElement>(null);

    const filteredFAQSchemas = React.useMemo(
        () => faqSchemas.filter((faq) => faq.question && faq.answer),
        [faqSchemas]
    );

    if (filteredFAQSchemas.length === 0 && !footerContent) {
        return null;
    }
    const defaultOpen = footerContent
        ? ['footerContent']
        : filteredFAQSchemas[0]?.question
          ? [filteredFAQSchemas[0]?.question]
          : [];

    return (
        <div className="mb-16 px-6 md:px-12">
            <div className="flex flex-col gap-12">
                {/* Footer content */}
                {footerContent && (
                    <div
                        className="faq-footer"
                        dangerouslySetInnerHTML={{ __html: footerContent }}
                    />
                )}

                {/* Render FAQs only if present */}
                {filteredFAQSchemas.length > 0 && (
                    <div>
                        <h4 className="faq-footer-title">FAQs</h4>
                        <Accordion type="multiple" defaultValue={defaultOpen}>
                            {filteredFAQSchemas.map((faq) => (
                                <AccordionItem
                                    key={faq.title}
                                    value={faq.question || ''}
                                >
                                    <div ref={openedItemRef}>
                                        <AccordionTrigger
                                            variant="narrow"
                                            icon="plus"
                                            openIcon="minus"
                                        >
                                            <div className="flex w-full items-center justify-between">
                                                <span
                                                    className="text-left text-xl font-medium"
                                                    dangerouslySetInnerHTML={{
                                                        __html: faq.title,
                                                    }}
                                                />
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent>
                                            <div
                                                className="grid py-2"
                                                dangerouslySetInnerHTML={{
                                                    __html: faq.answer || '',
                                                }}
                                            />
                                        </AccordionContent>
                                    </div>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </div>
                )}
            </div>
        </div>
    );
};
